'use client';

import * as React from 'react';

import { LOOP3_APPS, visibleAppsForUser, type Loop3App } from '../apps';
import { cn } from '../lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from './dropdown-menu';

type IconComponent = React.ComponentType<React.SVGProps<SVGSVGElement>>;

/** The Loop3 infinity (favicon mark), shown in place of the wordmark when the sidebar is collapsed to icons. */
function Loop3Mark(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="50 140 412 230" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden {...props}>
      <path
        d="M213 314C190 334 140 342 107 314C72 284 72 226 107 196C140 168 192 172 220 214C240 244 262 282 282 306C310 342 362 350 410 322C445 292 445 234 410 204C375 176 322 182 298 202"
        stroke="var(--l3-violet)"
        strokeWidth="40"
        strokeLinecap="round"
      />
    </svg>
  );
}

/**
 * Inline Loop3 wordmark — "loop" uses `currentColor` so it inherits the
 * surrounding text color (auto-adapts to dark mode); the "3" is locked to
 * the brand violet, which reads on both backgrounds.
 */
function Loop3Wordmark(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 106 35"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
      {...props}
    >
      <g clipPath="url(#l3wm-clip)">
        <path
          d="M34.6279 22.2603C34.2984 21.7755 33.7745 21.4652 33.1915 21.4065C32.5984 21.3478 32.0086 21.5541 31.5794 21.9735C31.4611 22.0875 31.3395 22.1999 31.2144 22.3039C30.0721 23.2566 28.6188 23.7817 27.1233 23.7817C25.2865 23.7817 23.5341 22.9883 22.3191 21.6044C21.9034 21.1297 21.3052 20.8781 20.68 20.9167C20.0683 20.9519 19.5174 21.2622 19.171 21.7655L19.1017 21.8644C18.5913 22.6058 18.6539 23.6005 19.2571 24.2815C21.2511 26.5393 24.1188 27.8342 27.1233 27.8342C29.5787 27.8342 31.9647 26.972 33.8421 25.407C34.0483 25.2343 34.2511 25.0514 34.4454 24.8602C35.123 24.196 35.2312 23.1443 34.7006 22.3626L34.6296 22.2586L34.6279 22.2603Z"
          fill="currentColor"
        />
        <path
          d="M47.7953 7.10702C44.904 6.97115 42.1648 7.9826 40.0846 9.95016C39.3918 10.606 39.2583 11.6745 39.7686 12.4897L39.8091 12.5551C40.1268 13.065 40.6524 13.4022 41.2506 13.4827C41.8623 13.5632 42.4757 13.3686 42.932 12.9476C44.2872 11.6963 46.0429 11.0472 47.7463 11.1646C50.9654 11.386 53.5593 14.0715 53.6523 17.2786C53.703 18.9996 53.0642 20.6283 51.8543 21.8645C50.6444 23.1024 49.0238 23.7834 47.2917 23.7834C45.7658 23.7834 44.2889 23.24 43.1364 22.2503L42.8458 22.0172C41.4753 20.791 40.262 18.7245 38.9811 16.5439C37.2457 13.5917 35.4527 10.5389 32.7879 8.75755C32.7068 8.7022 32.6257 8.6502 32.5378 8.59484C30.9105 7.61358 29.0381 7.0936 27.1235 7.0936C25.0248 7.0936 22.9953 7.71255 21.2514 8.88671C21.0131 9.05277 20.7833 9.22553 20.5704 9.39495C18.1978 11.2837 16.3289 14.0362 14.5208 16.6982L14.5055 16.72C12.4727 19.7175 10.5513 22.5506 8.34779 23.4865C7.45555 23.8656 6.43151 23.8136 5.60687 23.349C4.28373 22.6026 4.08433 21.0057 4.08433 15.9015V2.02626C4.08433 0.909135 3.16844 0 2.04132 0C0.914201 0 0 0.909135 0 2.02626V15.9031C0 16.4416 0 16.9599 0.00506951 17.4597C0.0236577 20.1402 0.219679 21.9165 0.640448 23.2182C0.890543 23.9914 1.51916 25.4457 2.91497 26.4857C3.78692 27.1348 5.50041 27.7907 7.08886 27.7907C9.29747 27.7907 11.0904 26.9922 13.0878 25.1203C14.8604 23.4563 16.3965 21.1936 17.8818 19.0046L17.8937 18.9862C19.6663 16.3695 21.5015 13.6639 23.5884 12.2113C24.6294 11.5168 25.8511 11.1495 27.1235 11.1495C28.2608 11.1495 29.3744 11.4497 30.3494 12.02L30.368 12.0318C32.3215 13.2579 33.9133 15.9669 35.4527 18.5886C37.0091 21.2389 38.6178 23.978 40.8399 25.6285L40.943 25.704C42.7545 27.0811 45.0037 27.8393 47.2917 27.8393C50.0901 27.8393 52.7195 26.7507 54.6983 24.7764C56.6771 22.8005 57.7569 20.1821 57.7383 17.4027C57.7214 14.7525 56.6855 12.228 54.8216 10.2974C52.9561 8.36672 50.4602 7.2345 47.7936 7.10869"
          fill="currentColor"
        />
        <path
          d="M70.9231 7.11203C68.6131 7.11203 66.3808 7.87188 64.5592 9.26409V9.20035C64.5592 8.08323 63.6433 7.17409 62.5179 7.17409C61.3925 7.17409 60.4766 8.08323 60.4766 9.20035V32.9737C60.4766 34.0908 61.3925 35 62.5179 35C63.6433 35 64.5592 34.0908 64.5592 32.9737V25.7006C66.3808 27.0929 68.6114 27.8527 70.9231 27.8527C76.6838 27.8527 81.3714 23.2014 81.3714 17.4815C81.3714 11.7617 76.6855 7.11035 70.9231 7.11035V7.11203ZM77.287 17.4815C77.287 20.9654 74.4312 23.7985 70.9231 23.7985C67.415 23.7985 64.5592 20.9654 64.5592 17.4815C64.5592 13.9976 67.4133 11.1646 70.9231 11.1646C74.4329 11.1646 77.287 13.9976 77.287 17.4815Z"
          fill="currentColor"
        />
        <path
          d="M104.083 13.8835C105.361 15.269 106.001 16.985 106.001 19.0297C106.001 20.6098 105.584 22.0657 104.749 23.3992C103.914 24.7327 102.642 25.8012 100.933 26.603C99.2247 27.4048 97.131 27.8074 94.652 27.8074C92.7205 27.8074 90.8229 27.5541 88.959 27.0492C88.158 26.8328 87.4077 26.5762 86.7098 26.2793C85.2768 25.6721 84.6736 23.9762 85.3799 22.5974C86.0254 21.3377 87.553 20.7658 88.8542 21.3444C89.3121 21.5474 89.7904 21.7269 90.2906 21.8829C91.6593 22.3106 93.0619 22.5236 94.4966 22.5236C96.0867 22.5236 97.3389 22.22 98.2531 21.6111C99.1656 21.0023 99.6235 20.1418 99.6235 19.028C99.6235 16.8005 97.9151 15.6884 94.4966 15.6884C92.898 15.6884 91.6002 14.4018 91.6002 12.8133V12.6188C91.6002 11.9176 91.8587 11.2416 92.3251 10.7166L97.2341 5.19976H88.1174C86.7132 5.19976 85.5742 4.06922 85.5742 2.67532C85.5742 1.28143 86.7132 0.150879 88.1174 0.150879H101.763C103.41 0.150879 104.747 1.47768 104.747 3.11311C104.747 3.83271 104.483 4.52714 104.005 5.06725L98.564 11.2198C100.964 11.609 102.804 12.4963 104.081 13.8802"
          fill="#8E7BFF"
        />
      </g>
      <defs>
        <clipPath id="l3wm-clip">
          <rect width="106" height="35" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
}

interface AppSwitcherProps {
  /** ID of the currently-rendered app (matches LOOP3_APPS[].id). */
  currentAppId: Loop3App['id'];
  /** Map of app id → icon component. Consumer-provided to keep this package lucide-free. */
  icons?: Partial<Record<Loop3App['id'], IconComponent>>;
  /** Hide the wordmark on small widths (an icon-collapsed sidebar swaps it for the mark on its own). */
  compact?: boolean;
  /**
   * Apps to consider. Defaults to the full LOOP3_APPS registry. Rarely
   * overridden — admin gating is handled by `isAdmin` below, not by
   * hand-filtering here.
   */
  apps?: readonly Loop3App[];
  /**
   * Whether the current user is an admin. Apps marked `adminOnly` in the
   * registry are hidden unless this is true. Defaults to `false` so a
   * consumer that forgets to pass it fails closed (hides admin apps) rather
   * than leaking them to non-admins.
   */
  isAdmin?: boolean;
  className?: string;
}

export function AppSwitcher({
  currentAppId,
  icons,
  compact = false,
  apps = LOOP3_APPS,
  isAdmin = false,
  className,
}: AppSwitcherProps) {
  const visibleApps = visibleAppsForUser(isAdmin, apps);
  const currentApp = visibleApps.find((a) => a.id === currentAppId);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={cn(
          'group/switcher inline-flex w-fit cursor-pointer items-center gap-2.5 rounded-md px-2 py-1.5 group-data-[collapsible=icon]:w-full group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-0.5 text-left outline-none transition-colors',
          'hover:bg-[var(--l3-violet-soft)] focus-visible:ring-2 focus-visible:ring-[var(--l3-violet)]',
          'data-[popup-open]:bg-[var(--l3-violet-soft)]',
          className,
        )}
      >
        <Loop3Mark className="hidden h-3.5 w-auto shrink-0 group-data-[collapsible=icon]:block" />
        {!compact && (
          <Loop3Wordmark className="h-4 w-auto shrink-0 group-data-[collapsible=icon]:hidden" />
        )}
        <span className="h-3.5 w-px bg-foreground/20 shrink-0 group-data-[collapsible=icon]:hidden" aria-hidden />
        <span className="text-[0.9rem] tracking-tight opacity-85 truncate group-data-[collapsible=icon]:sr-only">
          {currentApp?.name ?? 'Loop3'}
        </span>
        <svg
          aria-hidden
          viewBox="0 0 12 12"
          className="size-2.5 shrink-0 opacity-40 transition-transform group-data-[collapsible=icon]:hidden group-hover/switcher:opacity-80 group-data-[popup-open]/switcher:rotate-180"
        >
          <path
            d="M2 4.5L6 8.5L10 4.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="min-w-[240px]">
        <DropdownMenuLabel>Loop3 apps</DropdownMenuLabel>
        {visibleApps.map((app) => {
          const Icon = icons?.[app.id];
          const isCurrent = app.id === currentAppId;

          return (
            <DropdownMenuItem
              key={app.id}
              render={
                <a
                  href={app.url}
                  target={isCurrent ? undefined : '_blank'}
                  rel={isCurrent ? undefined : 'noopener noreferrer'}
                  aria-current={isCurrent ? 'page' : undefined}
                />
              }
              className={cn(
                'mt-0.5 first:mt-0',
                isCurrent &&
                  'bg-[var(--l3-violet-soft)] text-[var(--l3-violet)]',
              )}
            >
              {Icon && <Icon className="size-4 mt-0.5 shrink-0 opacity-80" />}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="text-sm font-medium">{app.name}</span>
                  {isCurrent && (
                    <span className="eyebrow opacity-70">Current</span>
                  )}
                </div>
                {app.description && (
                  <p className="mt-0.5 text-[0.7rem] leading-snug opacity-60">
                    {app.description}
                  </p>
                )}
              </div>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
